import {
  APPROVAL_ALERT_KEY_PREFIX,
  ASK_ALERT_KEY_PREFIX,
  SUGGESTION_SOUND_THROTTLE_MS,
} from '../../utils/constants/suggestionTabAlerts';

export type SuggestionTabAlertsState = {
  unseen: Record<string, number>;
  seenUntil: number;
  lastSoundAt: number;
};

export type IncomingSuggestion = {
  entryId: string;
  createdAt: number | undefined;
  isThisTabVisible: boolean;
  now: number;
};

export const EMPTY_SUGGESTION_TAB_ALERTS_STATE: SuggestionTabAlertsState = {
  unseen: {},
  seenUntil: 0,
  lastSoundAt: 0,
};

export function applyIncomingSuggestion(
  state: SuggestionTabAlertsState,
  { entryId, createdAt, isThisTabVisible, now }: IncomingSuggestion,
): SuggestionTabAlertsState {
  const effectiveCreatedAt = createdAt ?? now;
  if (isThisTabVisible) {
    return {
      ...state,
      unseen: {},
      seenUntil: Math.max(state.seenUntil, effectiveCreatedAt),
    };
  }
  if (effectiveCreatedAt <= state.seenUntil || entryId in state.unseen)
    return state;
  return {
    ...state,
    unseen: { ...state.unseen, [entryId]: effectiveCreatedAt },
  };
}

export function removeUnseen(
  state: SuggestionTabAlertsState,
  key: string,
): SuggestionTabAlertsState {
  if (!(key in state.unseen)) return state;
  const { [key]: _removed, ...unseen } = state.unseen;
  return { ...state, unseen };
}

export function markAllSeen(
  state: SuggestionTabAlertsState,
): SuggestionTabAlertsState {
  if (countUnseen(state) === 0) return state;
  const seenUntil = Math.max(state.seenUntil, ...Object.values(state.unseen));
  return { ...state, unseen: {}, seenUntil };
}

export function shouldPlaySound(
  state: SuggestionTabAlertsState,
  now: number,
): boolean {
  return now - state.lastSoundAt >= SUGGESTION_SOUND_THROTTLE_MS;
}

export function stampSound(
  state: SuggestionTabAlertsState,
  now: number,
): SuggestionTabAlertsState {
  return { ...state, lastSoundAt: now };
}

export const buildApprovalAlertKey = (entryId: string) =>
  `${APPROVAL_ALERT_KEY_PREFIX}${entryId}`;

export const buildAskAlertKey = (conversationId: string) =>
  `${ASK_ALERT_KEY_PREFIX}${conversationId}`;

export function hasAttentionItems(state: SuggestionTabAlertsState): boolean {
  return Object.keys(state.unseen).some(
    (key) =>
      key.startsWith(APPROVAL_ALERT_KEY_PREFIX) ||
      key.startsWith(ASK_ALERT_KEY_PREFIX),
  );
}

export function countUnseen(state: SuggestionTabAlertsState): number {
  return Object.keys(state.unseen).length;
}

function isUnseenRecord(value: unknown): value is Record<string, number> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every((entry) => typeof entry === 'number')
  );
}

function isSuggestionTabAlertsState(
  value: unknown,
): value is SuggestionTabAlertsState {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    isUnseenRecord(candidate.unseen) &&
    typeof candidate.seenUntil === 'number' &&
    typeof candidate.lastSoundAt === 'number'
  );
}

export function parseSuggestionTabAlertsState(
  raw: string | null,
): SuggestionTabAlertsState {
  if (raw === null) return EMPTY_SUGGESTION_TAB_ALERTS_STATE;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isSuggestionTabAlertsState(parsed))
      return EMPTY_SUGGESTION_TAB_ALERTS_STATE;
    return {
      unseen: parsed.unseen,
      seenUntil: parsed.seenUntil,
      lastSoundAt: parsed.lastSoundAt,
    };
  } catch {
    return EMPTY_SUGGESTION_TAB_ALERTS_STATE;
  }
}
